import * as React from "react";
import { LazyImage } from "../../common/components/lazy";

const IMAGE_SERVER_HOST_REGEXP = /^(https:\/\/(?:d2v9k5u4v94ulw|d2fde157oxqkl4).cloudfront.net)(\/.+)/;
const facebookHost = "https://graph.facebook.com";

type FacebookGeometry = "square" | "large";

interface Geometry {
  dw?: number;
  dh?: number;
  da: string;
  ds: string;
  cw?: number;
  ch?: number;
  cc: string;
  q: string;
  blur: string;
  angle: string;
  of: string;
}

// This method has same logic as backend. Please check Image#parse_geometry if you found a problem.
// @see: https://github.com/wantedly/wantedly/blob/master/app/models/image.rb
function parseGeometry(geometry: string): Geometry {
  // tslint:disable-next-line:max-line-length
  const r = /^(\d+x?)?(x(\d+))?(\^|#|!|>|m)?((q(100|[1-9][0-9]|[1-9]))|(r(90|180|270))|(b(\d+x\d+))|(_(jpeg|jpg|gif|png))|(c(([a-zA-Z]|[0-9])([a-zA-Z]|[0-9])([a-zA-Z]|[0-9])([a-zA-Z]|[0-9])([a-zA-Z]|[0-9])([a-zA-Z]|[0-9]))))*$/;
  const m = geometry.match(r);

  if (!m) {
    throw new Error("Malformed size for image!");
  }
  const width = m[1] ? parseInt(m[1].replace("x", ""), 10) : undefined;
  const height = m[3] ? parseInt(m[3], 10) : undefined;

  let aspect;
  switch (m[4]) {
    case "^":
      aspect = "s";
      break;
    case "!":
      aspect = "n";
      break;
    case "#":
      aspect = "s";
      break;
    case "m":
      aspect = "l";
      break;
    default:
      aspect = "l";
  }
  const scallingControl = m[4] === ">" ? "n" : "s";

  const canvasWidth = m[4] === "#" || m[4] === "m" ? width : undefined;
  const canvasHeight = m[4] === "#" || m[4] === "m" ? height : undefined;
  const canvasColor = m[15] ? m[15] : "FFFFFF";
  const [quality, blur, rotate, format] = [m[7], m[11], m[9], m[13]];

  return {
    dw: width,
    dh: height,
    da: aspect,
    ds: scallingControl,
    cw: canvasWidth,
    ch: canvasHeight,
    cc: canvasColor,
    q: quality,
    blur,
    angle: rotate,
    of: format,
  };
}

export const buildUrl = (baseUrl: string, size: string, x2: boolean = true) => {
  const cat = getImageUrlCategory(baseUrl);
  let isFacebookGeometry = false;
  if (cat === "facebook") {
    switch (size as FacebookGeometry) {
      case "square": {
        size = "50x50#";
        isFacebookGeometry = true;
        break;
      }
      case "large": {
        size = "200x200";
        isFacebookGeometry = true;
        break;
      }
    }
  }

  const geometry = parseGeometry(size);
  const width = geometry.dw;
  const height = geometry.dh;
  let url = baseUrl;
  const paramsArray: string[] = [];

  if (x2) {
    geometry.dw = geometry.dw && geometry.dw * 2;
    geometry.dh = geometry.dh && geometry.dh * 2;
    geometry.cw = geometry.cw && geometry.cw * 2;
    geometry.ch = geometry.ch && geometry.ch * 2;
  }
  Object.entries(geometry).forEach(([key, val]) => {
    if (val) {
      paramsArray.push(`${key}=${val}`);
    }
  });
  switch (cat) {
    case "image-server": {
      const res = baseUrl.match(IMAGE_SERVER_HOST_REGEXP)!;
      const [host, path] = res.slice(1, 3);
      url = `${host}/small_light(${paramsArray.join(",")})${path}`;
      break;
    }
    case "facebook": {
      const idx = url.indexOf("?");
      if (idx >= 0) {
        url = url.substr(0, idx);
      }
      if (!isFacebookGeometry && geometry.dw) {
        url = `${url}?width=${geometry.dw}&height=${geometry.dh}`;
      } else {
        url = `${url}?type=${cat || "large"}`;
      }
      break;
    }
  }
  return { url, width, height };
};

type ImageUrlCategory = "image-server" | "facebook" | "other";

function getImageUrlCategory(url: string): ImageUrlCategory {
  if (url.match(IMAGE_SERVER_HOST_REGEXP)) {
    return "image-server";
  } else if (url.startsWith(facebookHost)) {
    return "facebook";
  }
  return "other";
}

function onImageLoadErrorFunctionFactory(fallbackUrl: string) {
  return function onError(event: React.ChangeEvent<HTMLImageElement>) {
    event.target.src = fallbackUrl;
  };
}

export type Image = WtdModels.Partial<WtdModels.Image, "url">;

export type ImageProps = {
  image?: Image;
  size: string;
  x2?: boolean;
  background?: boolean;
  lazy?: boolean;
  fallbackUrl?: string;
} & React.ImgHTMLAttributes<any>;

const Image: React.SFC<ImageProps> = ({ image, size, x2, background, lazy, fallbackUrl, ...rest }) => {
  const geometry = parseGeometry(size);
  if (!image || !geometry) {
    return null;
  }
  const { url } = buildUrl(image.url, size, x2);
  if (lazy) {
    return <LazyImage src={url} background={background!} fallbackUrl={fallbackUrl} {...rest} />;
  }

  if (background) {
    if (fallbackUrl) {
      return <div style={{ backgroundImage: `url('${url}'), url('${fallbackUrl}')` }} {...rest} />;
    }
    return <div style={{ backgroundImage: `url('${url}')` }} {...rest} />;
  }
  if (fallbackUrl) {
    return <img src={url} onError={onImageLoadErrorFunctionFactory(fallbackUrl)} {...rest} />;
  }
  return <img src={url} {...rest} />;
};

Image.defaultProps = {
  x2: true,
  background: false,
  lazy: true,
};

export default Image;



// WEBPACK FOOTER //
// ./frontend/src/common/components/Image.tsx